import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { CaretLeft, Pause, Play } from '@phosphor-icons/react';
import MobileDetect from 'mobile-detect';

import { fetchAPI } from '@/api/fetch';
import { apiSubmitScreeningAnswer } from '@/api/services';
import { InterviewAgentStep, useInterviewAgent } from '@/store/InterviewAgent';

interface ScreeningProps {
  screeningType: 'practice' | 'official';
}

export default function Screening({ screeningType }: ScreeningProps) {
  const {
    screening,
    accessToken,
    practiceQuestions,
    questions,
    currentQuestionId,
    setCurrentQuestionId,
    submitQuestion,
    updateQuestionState,
    recordedStream,
    setStep,
    stopCounter,
    counter
  } = useInterviewAgent();

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTimer, setRecordingTimer] = useState(0);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState<
    'idle' | 'recording' | 'processing' | 'playback'
  >('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [retakeCount, setRetakeCount] = useState(0);

  const recorder = useRef<MediaRecorder | null>(null);
  const timerInterval = useRef<NodeJS.Timeout | null>(null);
  const recordedChunks = useRef<Blob[]>([]);
  const mimeType = useRef<string>('video/webm');
  const videoRef = useRef<HTMLVideoElement>(null);

  const relevantQuestions = useMemo(() => {
    return screeningType === 'practice' ? practiceQuestions : questions;
  }, [screeningType, practiceQuestions, questions]);

  const currentQuestionIndex = useMemo(() => {
    const idx = relevantQuestions.findIndex(
      (question) => question.questionId === currentQuestionId
    );
    return idx === -1 ? 0 : idx;
  }, [relevantQuestions, currentQuestionId]);

  const currentQuestion = useMemo(() => {
    const question = relevantQuestions.find(
      (question) => question.questionId === currentQuestionId
    );
    return question || relevantQuestions[0];
  }, [relevantQuestions, currentQuestionId]);

  const previousQuestion = useMemo(() => {
    return screeningType === 'practice'
      ? practiceQuestions[currentQuestionIndex - 1]
      : questions[currentQuestionIndex - 1];
  }, [currentQuestionIndex, practiceQuestions, questions, screeningType]);

  const retakeLeft = useMemo(() => {
    if (screeningType !== 'official') return -1;
    if (!screening) return -1;
    if (screening.retake_limit === -1) return -1;
    return screening.retake_limit - retakeCount;
  }, [screeningType, screening, retakeCount]);

  const isFirstQuestion = currentQuestionIndex === 0;

  const isLastQuestion =
    screeningType === 'practice'
      ? currentQuestionIndex === practiceQuestions.length - 1
      : currentQuestionIndex === questions.length - 1;

  useEffect(() => {
    if (screeningType === 'official') {
      const question = questions.find((question) => question.questionId === currentQuestionId);
      if (question) {
        setHasRecorded(question.completed);
        if (question.videoBlob && videoRef.current) {
          try {
            const url = URL.createObjectURL(question.videoBlob);
            videoRef.current.srcObject = null;
            videoRef.current.src = url;
            videoRef.current.muted = false;
            videoRef.current.loop = false;
            setRecordingStatus('playback');
          } catch (error) {
            console.error('Failed to create object URL for video blob:', error);
            setPermissionError('Failed to load saved video. Please try recording again.');
          }
        }
      }
    }
  }, [currentQuestionId, questions, screeningType]);

  useEffect(() => {
    if (!recordedStream) {
      setStep(InterviewAgentStep.DeviceTest);
    }
  }, [recordedStream, setStep]);

  // Initialize currentQuestionId if not set
  useEffect(() => {
    if (questions.length > 0 && !currentQuestionId) {
      setCurrentQuestionId(questions[0].questionId);
    }
  }, [questions, currentQuestionId, setCurrentQuestionId]);

  const loadSavedVideoState = useCallback(() => {
    if (!currentQuestion?.questionId) return;

    const savedQuestion = questions.find((q) => q.questionId === currentQuestion.questionId);
    if (!savedQuestion) return;

    // Restore retake count
    if (savedQuestion.retakeCount > 0) {
      setRetakeCount(savedQuestion.retakeCount);
    }

    // Restore video if available
    if (savedQuestion.videoBlob) {
      const video = videoRef.current;
      if (video) {
        try {
          const url = URL.createObjectURL(savedQuestion.videoBlob);
          video.srcObject = null;
          video.src = url;
          video.muted = false;
          video.loop = false;
          setHasRecorded(true);
          setRecordingStatus('playback');
        } catch (error) {
          console.error('Failed to create object URL for saved video blob:', error);
          setPermissionError('Failed to load saved video. Please try recording again.');
        }
      }
    }
  }, [currentQuestion?.questionId, questions]);

  // Load saved video state when question changes
  useEffect(() => {
    if (currentQuestionId && screeningType === 'official') {
      // Small delay to ensure video element is ready
      setTimeout(() => {
        loadSavedVideoState();
      }, 100);
    }
  }, [currentQuestionId, screeningType, loadSavedVideoState]);

  useEffect(() => {
    const md = new MobileDetect(navigator.userAgent);
    const isIphone = md.is('iPhone');

    // Check for supported MIME types with audio
    const supportedTypes = [
      'video/mp4;codecs=h264,aac',
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm'
    ];

    let selectedType = 'video/mp4';
    for (const type of supportedTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        selectedType = type;
        break;
      }
    }

    // Fallback for iPhone
    if (isIphone && MediaRecorder.isTypeSupported('video/mp4')) {
      selectedType = 'video/mp4';
    }

    mimeType.current = selectedType;
  }, []);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsVideoPlaying(true);
    const handlePause = () => setIsVideoPlaying(false);
    const handleEnded = () => setIsVideoPlaying(false);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  const toggleVideoPlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const resetToCamera = () => {
    // Clean up any existing MediaRecorder
    if (recorder.current) {
      if (recorder.current.state !== 'inactive') {
        recorder.current.stop();
      }
      recorder.current = null;
    }

    // Clear recorded chunks
    recordedChunks.current = [];

    const video = videoRef.current;
    if (video && recordedStream) {
      video.srcObject = recordedStream;
      video.muted = true;
      video.loop = false;
      video.play();
    }
    setHasRecorded(false);
    setRecordingStatus('idle');
    setIsVideoPlaying(false);
    setRecordingTimer(0);
    stopCountdown();
  };

  const startRecording = async () => {
    if (!recordedStream) {
      setPermissionError('Media stream not available. Please restart the device test.');
      return;
    }

    setIsRecording(true);
    setRecordingTimer(0);
    setRecordingStatus('recording');

    const options: MediaRecorderOptions = {
      mimeType: mimeType.current,
      audioBitsPerSecond: 128000,
      videoBitsPerSecond: 2500000
    };

    try {
      recorder.current = new MediaRecorder(recordedStream as MediaStream, options);
      recorder.current.start(1000);
      recordedChunks.current = [];

      recorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.current?.push(event.data);
        }
      };

      recorder.current.onstop = () => {
        setRecordingStatus('processing');
        const blob = new Blob(recordedChunks.current, { type: mimeType.current });

        try {
          const url = URL.createObjectURL(blob);
          const video = videoRef.current;

          if (video) {
            video.srcObject = null;
            video.src = url;
            video.muted = false;
            video.loop = false;
            setRecordingStatus('playback');
          }

          recordedChunks.current = [];
          setHasRecorded(true);
        } catch (error) {
          console.error('Failed to create object URL for recorded blob:', error);
          setPermissionError('Failed to process recording. Please try again.');
          setRecordingStatus('idle');
        }
      };
    } catch (error) {
      console.error('MediaRecorder error:', error);
      try {
        recorder.current = new MediaRecorder(recordedStream as MediaStream);
        recorder.current.start(1000);
        recordedChunks.current = [];

        recorder.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunks.current?.push(event.data);
          }
        };

        recorder.current.onstop = () => {
          setRecordingStatus('processing');
          const blob = new Blob(recordedChunks.current, {
            type: recorder.current?.mimeType || 'video/webm'
          });

          try {
            const url = URL.createObjectURL(blob);
            const video = videoRef.current;

            if (video) {
              video.srcObject = null;
              video.src = url;
              video.muted = false;
              video.loop = false;
              setRecordingStatus('playback');
            }

            recordedChunks.current = [];
            setHasRecorded(true);
          } catch (error) {
            console.error('Failed to create object URL for fallback recorded blob:', error);
            setPermissionError('Failed to process recording. Please try again.');
            setRecordingStatus('idle');
          }
        };
      } catch (fallbackError) {
        console.error('Fallback MediaRecorder error:', fallbackError);
        setPermissionError('Recording failed. Please try again.');
        setIsRecording(false);
        setRecordingStatus('idle');
      }
    }
  };

  const stopRecording = () => {
    recorder.current?.stop();
    setIsRecording(false);
    stopCountdown();
    setRetakeCount((prev) => prev + 1);
  };

  const startCountdown = () => {
    // Clear any existing interval
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
    }

    timerInterval.current = setInterval(() => {
      setRecordingTimer((prev) => prev + 1);
    }, 1000);
  };

  const stopCountdown = () => {
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isRecording) {
      startCountdown();
    } else {
      stopCountdown();
    }
  }, [isRecording]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      stopCountdown();
    };
  }, []);

  const getStatusColor = () => {
    switch (recordingStatus) {
      case 'recording':
        return 'red.500';
      case 'processing':
        return 'yellow.500';
      case 'playback':
        return 'green.500';
      default:
        return 'gray.500';
    }
  };

  const getStatusText = () => {
    switch (recordingStatus) {
      case 'recording':
        return 'Recording...';
      case 'processing':
        return 'Processing...';
      case 'playback':
        return 'Playing back';
      default:
        return 'Ready';
    }
  };

  const handleOfficialSubmitAnswer = async () => {
    if (isSubmitting) return;

    if (currentQuestion.completed) {
      setRetakeCount(0);

      if (!isLastQuestion) {
        // Move to next question or finish
        const nextQuestion = questions[currentQuestionIndex + 1];
        setCurrentQuestionId(nextQuestion.questionId);
        resetToCamera();
      } else {
        setStep(InterviewAgentStep.Completed);
      }
      return;
    }

    if (!currentQuestion || !hasRecorded) return;

    if (!accessToken) return;

    if (!currentQuestion.questionId) {
      console.error('Question ID is undefined:', currentQuestion);
      setPermissionError('Question ID is missing. Please try again.');
      return;
    }

    const video = videoRef.current;
    if (!video || !video.src) return;

    setPermissionError(null);
    setIsSubmitting(true);

    try {
      // Convert video URL to File object
      const videoResponse = await fetch(video.src);
      const blob = await videoResponse.blob();
      const file = new File([blob], `question_${currentQuestion.questionId}.webm`, {
        type: blob.type
      });

      const response = await fetchAPI<void>({
        request: () =>
          apiSubmitScreeningAnswer(accessToken, {
            question_id: currentQuestion.questionId,
            file,
            retake_times: retakeCount,
            complete: isLastQuestion
          })
      });

      if (response.success) {
        submitQuestion(currentQuestion.questionId, blob, file, retakeCount);
        setRetakeCount(0);

        if (!isLastQuestion) {
          // Move to next question or finish
          const nextQuestion = questions[currentQuestionIndex + 1];
          setCurrentQuestionId(nextQuestion.questionId);
          resetToCamera();
        } else {
          stopCounter();
          setStep(InterviewAgentStep.Completed);
        }
      } else {
        console.error('Failed to submit answer:', response.message);
        setPermissionError('Failed to submit answer. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      setPermissionError('Failed to submit answer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePracticeSubmitAnswer = async () => {
    if (isSubmitting) return;

    if (!currentQuestion || !hasRecorded) return;

    if (!accessToken) return;

    if (!currentQuestion.questionId) {
      console.error('Question ID is undefined:', currentQuestion);
      setPermissionError('Question ID is missing. Please try again.');
      return;
    }

    const video = videoRef.current;
    if (!video || !video.src) return;

    setPermissionError(null);
    setIsSubmitting(true);

    try {
      if (!isLastQuestion) {
        // Move to next question or finish
        const nextQuestion = practiceQuestions[currentQuestionIndex + 1];
        setCurrentQuestionId(nextQuestion.questionId);
        resetToCamera();
      } else {
        setStep(InterviewAgentStep.StartInterview);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      setPermissionError('Failed to submit answer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveCurrentQuestionState = async () => {
    if (!currentQuestion?.questionId) return;

    // Save current video if recorded
    let videoBlob: Blob | null = null;
    const video = videoRef.current;
    if (hasRecorded && video?.src && !video.srcObject) {
      try {
        const response = await fetch(video.src);
        videoBlob = await response.blob();
      } catch (error) {
        console.error('Failed to save video blob:', error);
      }
    }

    // Update store with current state
    updateQuestionState(currentQuestion.questionId, videoBlob, retakeCount);
  };

  const goToPreviousQuestion = async () => {
    if (isSubmitting) return;
    if (currentQuestionIndex === 0) return;

    const prevQuestion =
      screeningType === 'practice'
        ? practiceQuestions[currentQuestionIndex - 1]
        : questions[currentQuestionIndex - 1];

    if (prevQuestion.videoBlob) {
      // Save current question state before navigating
      await saveCurrentQuestionState();

      setCurrentQuestionId(prevQuestion.questionId);
      resetToCamera();
    }
  };

  // Initialize video stream when component mounts
  useEffect(() => {
    const video = videoRef.current;
    if (video && recordedStream) {
      video.srcObject = recordedStream;
      video.muted = true;
      video.play();
    }
  }, [recordedStream]);

  return (
    <Stack w="100%" h="100%" p="0 60px" mt="80px">
      {/* Header */}
      <Flex mb="32px" align="center" gap="16px">
        {screeningType === 'official' && !isFirstQuestion && previousQuestion.videoBlob && (
          <Button
            variant="ghost"
            disabled={isRecording || isSubmitting}
            onClick={goToPreviousQuestion}
            _hover={{ bg: 'transparent' }}
          >
            <CaretLeft size={24} />
          </Button>
        )}
        <Heading as="h1" color="neutrals.900">
          {screeningType === 'practice'
            ? 'Practice Question'
            : `Question ${currentQuestionIndex + 1} of ${questions.length}`}
        </Heading>
      </Flex>

      <Flex gap="24px">
        {/* Video Container */}
        <Box
          pos="relative"
          w="100%"
          maxW="600px"
          h="375px"
          borderRadius="20px"
          overflow="hidden"
          boxShadow="0 10px 30px rgba(0, 0, 0, 0.2)"
          border="3px solid"
          borderColor={getStatusColor()}
          transition="all 0.3s ease"
          alignSelf="center"
        >
          <video
            ref={videoRef}
            id="screening-video"
            autoPlay={true}
            muted={true}
            playsInline={true}
            style={{
              width: '100%',
              height: '100%',
              background: '#1a1a1a',
              transform: 'rotateY(180deg)',
              objectFit: 'cover'
            }}
          />

          {/* Status Overlay */}
          <Box
            pos="absolute"
            top="16px"
            left="16px"
            bg="rgba(0, 0, 0, 0.7)"
            color="white"
            px="12px"
            py="6px"
            borderRadius="20px"
            fontSize="14px"
            fontWeight={600}
          >
            <Flex gap={2} align="center">
              <Box
                w="8px"
                h="8px"
                borderRadius="50%"
                bg={getStatusColor()}
                animation={recordingStatus === 'recording' ? 'pulse 1s infinite' : 'none'}
              />
              <Text>{getStatusText()}</Text>
            </Flex>
          </Box>

          {/* Recording Timer */}
          {isRecording && (
            <Box
              pos="absolute"
              top="16px"
              right="16px"
              bg="error.500"
              color="white"
              px="12px"
              py="6px"
              borderRadius="20px"
              fontSize="16px"
              fontWeight={700}
              fontFamily="mono"
            >
              {formatTime(recordingTimer)}
            </Box>
          )}

          {/* Play/Pause Button Overlay */}
          {hasRecorded && recordingStatus === 'playback' && (
            <Flex
              pos="absolute"
              inset="0"
              align="center"
              justify="center"
              bg="rgba(0, 0, 0, 0.3)"
              opacity={isVideoPlaying ? 0 : 1}
              transition="opacity 0.3s ease"
              _hover={{ opacity: 1 }}
              cursor="pointer"
              onClick={toggleVideoPlayback}
            >
              <Button
                size="lg"
                borderRadius="50%"
                w="80px"
                h="80px"
                bg="rgba(0, 0, 0, 0.8)"
                color="white"
                _hover={{ bg: 'rgba(0, 0, 0, 0.9)' }}
                _active={{ transform: 'scale(0.95)' }}
                transition="all 0.2s ease"
              >
                {isVideoPlaying ? <Pause size={32} /> : <Play size={32} />}
              </Button>
            </Flex>
          )}

          {/* No Camera Message */}
          {!recordedStream && (
            <Flex
              pos="absolute"
              inset="0"
              align="center"
              justify="center"
              bg="neutrals.900"
              color="white"
              textAlign="center"
              p="20px"
            >
              <Stack gap={3}>
                <Text fontSize="16px" fontWeight={500}>
                  Camera Access Required
                </Text>
                <Text fontSize="14px" opacity={0.8}>
                  Please complete the device test first
                </Text>
              </Stack>
            </Flex>
          )}
        </Box>

        {/* Question Text */}
        <Stack p="16px" fontSize="20px" color="neutrals.900" maxW="1000px">
          {screeningType === 'practice' && <Text fontWeight="bold">Practice Question:</Text>}
          <Text>{currentQuestion?.question}</Text>
        </Stack>
      </Flex>

      {/* Error Message */}
      {permissionError && (
        <Box
          bg="red.50"
          border="1px solid"
          borderColor="red.200"
          borderRadius="12px"
          p="16px"
          w="100%"
          maxW="600px"
          alignSelf="center"
        >
          <Text fontSize="14px" color="red.800" fontWeight={500}>
            ⚠️ {permissionError}
          </Text>
        </Box>
      )}

      {/* Action Buttons */}
      <Flex
        gap="24px"
        w="100%"
        maxW="600px"
        align="center"
        justify="center"
        alignSelf="center"
        mt="32px"
      >
        {!currentQuestion.completed && (
          <Button
            size="lg"
            fontSize="18px"
            fontWeight={600}
            w="320px"
            h="48px"
            color={isRecording ? 'neutrals.0' : hasRecorded ? 'primary.900' : 'neutrals.0'}
            borderRadius="8px"
            border="1px solid"
            borderColor={isRecording ? 'red.500' : 'primary.900'}
            bgColor={isRecording ? 'red.500' : hasRecorded ? 'neutrals.0' : 'primary.900'}
            _hover={{
              bg: isRecording ? 'red.700' : hasRecorded ? 'neutrals.100' : 'primary.700'
            }}
            _active={{
              bg: isRecording ? 'red.700' : 'primary.900'
            }}
            onClick={() => {
              if (isRecording) {
                stopRecording();
              } else {
                if (hasRecorded) {
                  resetToCamera();
                  // Wait for camera to reset before starting recording
                  setTimeout(() => {
                    startRecording();
                  }, 300);
                } else {
                  startRecording();
                }
              }
            }}
            transition="all 0.2s ease"
            disabled={
              !recordedStream || isSubmitting || (screeningType === 'official' && retakeLeft === 0)
            }
          >
            {isRecording
              ? 'Stop Recording'
              : hasRecorded
              ? `Retake${
                  screeningType === 'official' && retakeLeft !== -1 ? ` (${retakeLeft} left)` : ''
                }`
              : 'Start Recording'}
          </Button>
        )}

        {hasRecorded && !isRecording && (
          <Button
            size="lg"
            fontSize="18px"
            fontWeight={600}
            w="320px"
            h="48px"
            color="white"
            borderRadius="8px"
            bgColor="primary.900"
            _hover={{
              bg: 'primary.700'
            }}
            onClick={
              screeningType === 'official' ? handleOfficialSubmitAnswer : handlePracticeSubmitAnswer
            }
            transition="all 0.2s ease"
            loading={isSubmitting}
          >
            {screeningType === 'official' && isLastQuestion ? 'Submit' : 'Next'}
          </Button>
        )}
      </Flex>

      {screeningType === 'official' && (
        <Flex alignSelf="flex-end" px="32px">
          <Flex align="center" gap="8px" ml="16px">
            <Text color="neutrals.500" fontSize="16px">
              Time remaining:
            </Text>
            <Text
              color={counter > 600 ? 'neutrals.900' : 'error.500'}
              fontWeight="bold"
              fontSize="18px"
            >
              {(() => {
                // Show MM:SS format for the counter
                const minutes = Math.floor((counter || 0) / 60);
                const seconds = (counter || 0) % 60;
                return `${minutes.toString().padStart(2, '0')}:${seconds
                  .toString()
                  .padStart(2, '0')}`;
              })()}
            </Text>
          </Flex>
        </Flex>
      )}
    </Stack>
  );
}
